import { getApiEndpoints } from '@/services/api/endpoints';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import type { IDevice, IDeviceInfo } from '@/domain/entities/device';
import type { IProperty } from '@/domain/entities/property';
import type { SendPushNotificationRequest, SendPushNotificationResponse } from '@/domain/entities/push-notification';
import type { IHomeownerUser } from '@/domain/entities/user';
import { parseApplicationUserId } from '@/domain/identity';
import type {
  FetchHomeownerDevicesRequest,
  FetchHomeownerDevicesResponse,
  FetchHomeownerRequest,
  FetchHomeownerPropertiesRequest,
  FetchHomeownerPropertiesResponse,
  FetchHomeownersRequest,
  FetchHomeownersResponse,
} from './homeownersTypes';

type RawDevice = Omit<IDevice, 'userId' | 'deviceId'> & {
  userId?: string;
  userID?: string;
  deviceId?: string;
  installationID?: string;
  deviceInfo?: IDeviceInfo;
};

type RawDevicesResponse = {
  items: RawDevice[];
  count: number;
};

function toHomeownerKey(userId: string) {
  return userId.trim();
}

function toErrorMessage(error: Error & { body?: string }, fallback: string) {
  if (!error.body) return error.message || fallback;

  try {
    const body = JSON.parse(error.body) as { message?: string; data?: string | null };
    return body.data || body.message || error.message || fallback;
  } catch {
    return error.message || fallback;
  }
}

function toPushNotificationErrorMessage(error: Error & { body?: string }, fallback: string) {
  let message = error.message || fallback;
  if (!error.body) return message;

  try {
    const body = JSON.parse(error.body) as {
      message?: unknown;
      data?: unknown;
      fcmResponse?: { responses?: Array<{ success?: boolean; error?: unknown }> };
    };
    const payload =
      body.data && typeof body.data === 'object' && !Array.isArray(body.data) ? (body.data as typeof body) : body;

    if (typeof payload.message === 'string' && payload.message.trim()) {
      message = payload.message.trim();
    } else if (typeof body.message === 'string' && body.message.trim()) {
      message = body.message.trim();
    }

    const reasons = Array.from(
      new Set(
        (payload.fcmResponse?.responses ?? [])
          .filter((response) => response.success === false && typeof response.error === 'string')
          .map((response) => (response.error as string).trim())
          .filter(Boolean),
      ),
    );

    return reasons.length > 0 ? `${message} — FCM: ${reasons.join(', ')}` : message;
  } catch {
    return message;
  }
}

function normalizeDevice(raw: RawDevice): IDevice {
  return {
    ...raw,
    userId: raw.userId ?? raw.userID ?? '',
    deviceId: raw.deviceId ?? raw.installationID ?? raw.id,
  };
}

function normalizeHomeowner(homeowner: IHomeownerUser): IHomeownerUser {
  return {
    ...homeowner,
    applicationUserId: parseApplicationUserId(homeowner.applicationUserId),
  };
}

export const fetchHomeownerById = createAppAsyncThunk<IHomeownerUser, FetchHomeownerRequest>(
  'homeowners/fetchById',
  async ({ userId }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;
    const id = toHomeownerKey(userId);

    try {
      const endpoint = `${getApiEndpoints().core.homeowners.getHomeowner}?id=${encodeURIComponent(id)}`;
      logger?.info('[fetchHomeownerById] Fetching from:', endpoint);
      return normalizeHomeowner(await apiService.get<IHomeownerUser>(endpoint));
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[fetchHomeownerById] Error:', error.message);
      return rejectWithValue({
        message: error.status === 404 ? 'Home owner not found.' : error.message || 'Failed to load homeowner',
      });
    }
  },
  {
    condition: ({ userId, force }, { getState }) => {
      const id = toHomeownerKey(userId);
      if (!id) return false;

      const state = getState().homeowners;
      if (state.fetchingById[id]) return false;
      if (force) return true;
      if (state.byId[id] || state.hasFetchedById[id]) return false;
      return true;
    },
  },
);

export const fetchHomeowners = createAppAsyncThunk<FetchHomeownersResponse, FetchHomeownersRequest | void>(
  'homeowners/fetchList',
  async (request, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;
    const limit = Math.min(Math.max(request?.limit ?? 50, 1), 100);
    const params = new URLSearchParams({ limit: String(limit) });

    if (request?.nextToken) {
      params.set('nextToken', request.nextToken);
    }

    try {
      const endpoint = `${getApiEndpoints().core.homeowners.listHomeowners}?${params.toString()}`;
      logger?.info('[fetchHomeowners] Fetching from:', endpoint);
      const response = await apiService.get<FetchHomeownersResponse>(endpoint);
      return { ...response, items: response.items.map(normalizeHomeowner) };
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[fetchHomeowners] Error:', error.message);

      let errorMessage = error.message;
      if (error.body) {
        try {
          const body = JSON.parse(error.body) as { message?: string; data?: string };
          errorMessage = body.data || body.message || error.message;
        } catch {
          // keep original message
        }
      }

      return rejectWithValue({ message: errorMessage || 'Failed to load home owners' });
    }
  },
  {
    condition: (request, { getState }) => {
      const state = getState().homeowners;
      if (state.isListing) return false;
      if (request?.nextToken && !state.nextToken) return false;
      return true;
    },
  },
);

export const fetchHomeownerDevices = createAppAsyncThunk<FetchHomeownerDevicesResponse, FetchHomeownerDevicesRequest>(
  'homeowners/fetchDevices',
  async ({ userID }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;
    const id = toHomeownerKey(userID);

    try {
      const params = new URLSearchParams({ userID: id });
      const endpoint = `${getApiEndpoints().core.pushNotifications.getDevices}?${params.toString()}`;
      logger?.info('[fetchHomeownerDevices] Fetching from:', endpoint);
      const response = await apiService.get<RawDevicesResponse>(endpoint);
      return {
        items: response.items.map(normalizeDevice),
        count: response.count,
      };
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[fetchHomeownerDevices] Error:', error.message);
      return rejectWithValue({ message: toErrorMessage(error, 'Failed to load devices') });
    }
  },
  {
    condition: ({ userID, force }, { getState }) => {
      const id = toHomeownerKey(userID);
      if (!id) return false;

      const state = getState().homeowners;
      if (state.fetchingDevicesByUserId[id]) return false;
      if (force) return true;
      if (state.hasFetchedDevicesByUserId[id]) return false;
      return true;
    },
  },
);

export const fetchHomeownerProperties = createAppAsyncThunk<
  FetchHomeownerPropertiesResponse,
  FetchHomeownerPropertiesRequest
>(
  'homeowners/fetchProperties',
  async ({ userID }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;
    const id = toHomeownerKey(userID);

    try {
      const params = new URLSearchParams({ ownerID: id });
      const endpoint = `${getApiEndpoints().core.properties.getProperties}?${params.toString()}`;
      logger?.info('[fetchHomeownerProperties] Fetching membership-backed properties from:', endpoint);
      return await apiService.get<IProperty[]>(endpoint);
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[fetchHomeownerProperties] Error:', error.message);
      return rejectWithValue({ message: toErrorMessage(error, 'Failed to load properties') });
    }
  },
  {
    condition: ({ userID, force }, { getState }) => {
      const id = toHomeownerKey(userID);
      if (!id) return false;

      const state = getState().homeowners;
      if (state.fetchingPropertiesByUserId[id]) return false;
      if (force) return true;
      if (state.hasFetchedPropertiesByUserId[id]) return false;
      return true;
    },
  },
);

export const sendPushNotification = createAppAsyncThunk<SendPushNotificationResponse, SendPushNotificationRequest>(
  'homeowners/sendPushNotification',
  async (request, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().core.pushNotifications.send;
      logger?.info('[sendPushNotification] Sending push notification', {
        userID: request.userID,
        deviceID: request.deviceID,
      });
      return await apiService.post<SendPushNotificationResponse, SendPushNotificationRequest>(endpoint, request);
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[sendPushNotification] Error:', error.message);
      return rejectWithValue({ message: toPushNotificationErrorMessage(error, 'Failed to send push notification') });
    }
  },
);
